import { Breadcrumbs, Typography, Link } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { usePrincipalRhViewModel } from "../../viewModels/principal_rh_view_model";
import { PrincipaisDocGrid } from "../../../../../common/components/principaisDocGrid";
import { People, DoNotDisturb, Warning, Check, ThumbUp, AccessTime } from "@mui/icons-material";
import { ReportsTreinamentosFuncionariosModal } from "./components/reports_treinamentos_funcionarios_modal";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";

import AlertError from "../../../../../common/components/alert_error";
import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import StatCard from "../../../../../common/components/statCard";
import StatCardString from "../../../../../common/components/statCardString";

type TreinamentosPieChartProps = {
  quantidadeAvaliacoesTreinamentoEmAtraso: number;
  quantidadeAvaliacoesTreinamentoDentroPrazo: number;
  quantidadeAvaliacoesTreinamentoAvaliados: number;
};

const TreinamentosPieChart = ({
  quantidadeAvaliacoesTreinamentoEmAtraso,
  quantidadeAvaliacoesTreinamentoDentroPrazo,
  quantidadeAvaliacoesTreinamentoAvaliados,
}: TreinamentosPieChartProps) => {
  const total =
    quantidadeAvaliacoesTreinamentoEmAtraso +
    quantidadeAvaliacoesTreinamentoDentroPrazo +
    quantidadeAvaliacoesTreinamentoAvaliados;

  const seriesData = [
    {
      id: "em-atraso",
      value: quantidadeAvaliacoesTreinamentoEmAtraso,
      label: "Em atraso",
      color: "#ef4444",
    },
    {
      id: "dentro-prazo",
      value: quantidadeAvaliacoesTreinamentoDentroPrazo,
      label: "Dentro do prazo",
      color: "#14b8a6",
    },
    {
      id: "avaliados",
      value: quantidadeAvaliacoesTreinamentoAvaliados,
      label: "Avaliados",
      color: "#3b82f6",
    },
  ].filter((item) => item.value > 0);

  return (
    <div className="w-full rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-medium text-black dark:text-white">Avaliação de treinamentos</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">Distribuição por status das avaliações</p>
        </div>

        <div className="rounded-xl bg-zinc-100 px-4 py-2 text-right dark:bg-zinc-800">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Total</p>
          <p className="text-xl font-semibold text-black dark:text-white">{total}</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="flex justify-center lg:w-[400px] lg:flex-none">
          {total > 0 ? (
            <PieChart
              width={340}
              height={280}
              series={[
                {
                  data: seriesData,
                  innerRadius: 40,
                  outerRadius: 120,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  startAngle: -90,
                  endAngle: 270,
                  arcLabelMinAngle: 18,
                  arcLabel: (item) => String(item.value),
                },
              ]}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              hideLegend={true}
            />
          ) : (
            <div className="flex h-[280px] w-[340px] items-center justify-center rounded-full border border-dashed border-zinc-300 text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
              Sem dados para exibir
            </div>
          )}
        </div>

        <div className="grid flex-1 gap-3 sm:grid-cols-3 lg:grid-cols-1">
          {[
            {
              label: "Em atraso",
              value: quantidadeAvaliacoesTreinamentoEmAtraso,
              color: "bg-red-500",
            },
            {
              label: "Dentro do prazo",
              value: quantidadeAvaliacoesTreinamentoDentroPrazo,
              color: "bg-teal-500",
            },
            {
              label: "Avaliados",
              value: quantidadeAvaliacoesTreinamentoAvaliados,
              color: "bg-blue-500",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-3 rounded-xl border border-zinc-200 px-4 py-3 dark:border-zinc-800"
            >
              <span className={`h-3 w-3 rounded-full ${item.color}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-black dark:text-white">{item.label}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Quantidade de avaliações</p>
              </div>
              <strong className="text-lg text-black dark:text-white">{item.value}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * PrincipalRh
 */

export const PrincipalRh = () => {
  const viewModel = usePrincipalRhViewModel();
  return (
    <>
      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[10rem]">
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={() => viewModel.handleReportsTreinamentosFuncionariosOpenState(true)}>
                  Gerar relatório
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            RH
          </Link>
          <Typography className="text-primary-green">
            Principal RH
          </Typography>
        </Breadcrumbs>

        <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-3xl font-medium text-black dark:text-white">Principal RH</h1>
            <RefreshButton onClick={viewModel.handleReload} />
          </div>

          <div className="w-full justify-end flex gap-2"></div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                {
                  viewModel.dashboardRhInformation !== null && (
                    <div className="w-full flex flex-col gap-4 mb-8">

                      <TreinamentosPieChart
                        quantidadeAvaliacoesTreinamentoEmAtraso={viewModel.dashboardRhInformation.quantidadeAvaliacoesTreinamentoEmAtraso}
                        quantidadeAvaliacoesTreinamentoDentroPrazo={viewModel.dashboardRhInformation.quantidadeAvaliacoesTreinamentoDentroPrazo}
                        quantidadeAvaliacoesTreinamentoAvaliados={viewModel.dashboardRhInformation.quantidadeAvaliacoesTreinamentoAvaliados}
                      />

                      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                        <StatCard
                          title='Funcionários Ativos'
                          value={viewModel.dashboardRhInformation.quantidadeFuncionariosAtivos}
                          icon={People}
                          colorClass={{
                            bg: 'bg-teal-500',
                            text: 'text-white',
                            bgHover: 'bg-teal-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.funcionariosAtivos)}
                        />

                        <StatCard
                          title='Funcionários Demitidos'
                          value={viewModel.dashboardRhInformation.quantidadeFuncionariosDemitidos}
                          icon={DoNotDisturb}
                          colorClass={{
                            bg: 'bg-zinc-500',
                            text: 'text-white',
                            bgHover: 'bg-zinc-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.funcionariosDemitidos)}
                        />

                        <StatCard
                          title='Avalições de Habilidades Atrasadas'
                          value={viewModel.dashboardRhInformation.quantidadeAvaliacoesHabilidadesEmAtraso}
                          icon={Warning}
                          colorClass={{
                            bg: 'bg-red-500',
                            text: 'text-white',
                            bgHover: 'bg-red-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.avaliacoesHabilidadesEmAtraso)}
                        />

                        <StatCard
                          title='Treinamentos Realizados'
                          value={viewModel.dashboardRhInformation.quantidadeTreinamentosRealizados}
                          icon={Check}
                          colorClass={{
                            bg: 'bg-teal-500',
                            text: 'text-white',
                            bgHover: 'bg-teal-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.treinamentosRealizados)}
                        />

                        <StatCard
                          title='Treinamentos Não Realizados'
                          value={viewModel.dashboardRhInformation.quantidadeTreinamentosNaoRealizados}
                          icon={Warning}
                          colorClass={{
                            bg: 'bg-amber-500',
                            text: 'text-white',
                            bgHover: 'bg-amber-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.treinamentosNaoRealizados)}
                        />

                        <StatCard
                          title='Treinamentos Eficazes'
                          value={viewModel.dashboardRhInformation.quantidadeTreinamentosEficazes}
                          icon={ThumbUp}
                          colorClass={{
                            bg: 'bg-teal-500',
                            text: 'text-white',
                            bgHover: 'bg-teal-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.treinamentosEficazes)}
                        />

                        <StatCardString
                          title='Treinamentos Eficazes X Realizados'
                          value={
                            viewModel.dashboardRhInformation.quantidadeTreinamentosEficazesRealizados
                              ? new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 0 }).format(viewModel.dashboardRhInformation.quantidadeTreinamentosEficazesRealizados)
                              : new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 0 }).format(0)
                          }
                          icon={AccessTime}
                          colorClass={{
                            bg: 'bg-red-500',
                            text: 'text-white',
                            bgHover: 'bg-red-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.treinamentosEficazesRealizados)}
                        />

                        <StatCard
                          title='Documentos Vencidos'
                          value={viewModel.dashboardRhInformation.quantidadeDocumentosVencidos}
                          icon={Warning}
                          colorClass={{
                            bg: 'bg-red-500',
                            text: 'text-white',
                            bgHover: 'bg-red-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.documentosVencidos)}
                        />

                        <StatCard
                          title='Funcionários de Férias'
                          value={viewModel.dashboardRhInformation.quantidadeFuncionariosFerias}
                          icon={People}
                          colorClass={{
                            bg: 'bg-amber-500',
                            text: 'text-white',
                            bgHover: 'bg-amber-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.funcionariosFerias)}
                        />

                        <StatCard
                          title='Funcionários Afastados'
                          value={viewModel.dashboardRhInformation.quantidadeFuncionariosAfastados}
                          icon={People}
                          colorClass={{
                            bg: 'bg-amber-500',
                            text: 'text-white',
                            bgHover: 'bg-amber-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.funcionariosAfastados)}
                        />

                        <StatCard
                          title='Funcionários de Aviso Prévio'
                          value={viewModel.dashboardRhInformation.quantidadeFuncionariosAvisoPrevio}
                          icon={People}
                          colorClass={{
                            bg: 'bg-amber-500',
                            text: 'text-white',
                            bgHover: 'bg-amber-600',
                          }}
                          linkText='Ver detalhes'
                          onClick={() => viewModel.handleCardClick(viewModel.cardIds.funcionariosAvisoPrevio)}
                        />
                      </div>

                      <PrincipaisDocGrid
                        loading={viewModel.loading}
                        rows={viewModel.docLista}
                        totalItems={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onPageChange={(p) => viewModel.handlePageChange(p)}
                        onRowsPerPageChange={(p) => viewModel.handleRowsPerPageChange(p)}
                        onActionExportar={(IdArquivoModulo, NomeArquivo) =>
                          viewModel.handleDownloadFile(IdArquivoModulo, NomeArquivo)
                        }
                      />
                    </div>
                  )
                }
              </>
            )
        }
      </div>

      <ReportsTreinamentosFuncionariosModal
        open={viewModel.reportsTreinamentosFuncionariosModalOpen}
        saving={viewModel.loading}
        loading={viewModel.loading}
        funcionariosDropdown={viewModel.funcionariosDropdown}
        centroCustosDropdown={viewModel.centroCustosDropdown}
        onClose={() => viewModel.handleReportsTreinamentosFuncionariosOpenState(false)}
        onSubmit={viewModel.handleRelatorio}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />
    </>
  );
}